import { useMemo, useState } from "react";
import { useNavigate } from "@/lib/router-compat";
import { ArrowRight, Building2, CheckCircle2, Star, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import HeroBackgroundImage from "@/components/common/HeroBackgroundImage";
import {
  quoteServices,
  quoteOfferPath,
  quoteServiceAliases,
  type CustomerType,
} from "@/data/quoteServices";
import { getQuoteWidgetOptions, encodePrefill } from "@/data/quoteWidgetOptions";
import { CUSTOMER_RATING } from "@/config/rating";


interface QuoteHeroWidgetProps {
  title: string;
  subtitle: string;
  icon?: React.ReactNode;
  /** Tjänst som är förvald i widgeten (slug i quoteServices) */
  serviceSlug?: string;
  defaultCustomerType?: CustomerType;
  /** Förifylld ort, t.ex. kommunnamn på lokala sidor */
  city?: string;
}

/**
 * Hero med steg 1 av offertflödet som widget. Val skickas vidare till
 * /{tjanst}/offert där resterande steg fylls i.
 */
const QuoteHeroWidget = ({
  title,
  subtitle,
  icon,
  serviceSlug,
  defaultCustomerType = "private",
  city,
}: QuoteHeroWidgetProps) => {
  const navigate = useNavigate();
  const resolvedSlug = serviceSlug
    ? quoteServiceAliases[serviceSlug] ?? serviceSlug
    : undefined;
  const widgetOptions = getQuoteWidgetOptions(resolvedSlug);
  const [customerType, setCustomerType] = useState<CustomerType>(defaultCustomerType);
  const [selected, setSelected] = useState<string[]>(
    resolvedSlug && quoteServices.some((s) => s.slug === resolvedSlug) ? [resolvedSlug] : [],
  );
  const [optionId, setOptionId] = useState<string>(widgetOptions?.[0]?.id ?? "");
  const place = city ?? "";

  const available = useMemo(
    () => quoteServices.filter((s) => s.customerTypes.includes(customerType)),
    [customerType],
  );

  /** Val som är relevanta för vald kundtyp. */
  const optionsForType = useMemo(
    () =>
      (widgetOptions ?? []).filter((o) => {
        const svcs =
          customerType === "business" && o.businessServices ? o.businessServices : o.services;
        return svcs.some((slug) =>
          quoteServices.find((s) => s.slug === slug)?.customerTypes.includes(customerType),
        );
      }),
    [widgetOptions, customerType],
  );

  const changeCustomerType = (type: CustomerType) => {
    setCustomerType(type);
    setSelected((prev) =>
      prev.filter((slug) =>
        quoteServices.find((s) => s.slug === slug)?.customerTypes.includes(type),
      ),
    );
    const next = (widgetOptions ?? []).filter((o) => {
      const svcs = type === "business" && o.businessServices ? o.businessServices : o.services;
      return svcs.some((slug) =>
        quoteServices.find((s) => s.slug === slug)?.customerTypes.includes(type),
      );
    });
    if (next.length && !next.some((o) => o.id === optionId)) setOptionId(next[0]!.id);
  };

  const toggle = (slug: string) =>
    setSelected((prev) =>
      prev.includes(slug) ? prev.filter((s) => s !== slug) : [...prev, slug],
    );

  const start = () => {
    const chosenOption = widgetOptions?.find((o) => o.id === optionId) ?? widgetOptions?.[0];
    const optionServices = chosenOption
      ? (customerType === "business" && chosenOption.businessServices
          ? chosenOption.businessServices
          : chosenOption.services)
      : undefined;
    const services = optionServices
      ? optionServices.filter((slug) =>
          quoteServices.find((s) => s.slug === slug)?.customerTypes.includes(customerType),
        )
      : selected.length
        ? selected
        : available.slice(0, 1).map((s) => s.slug);
    const finalServices = services.length
      ? services
      : optionServices ?? available.slice(0, 1).map((s) => s.slug);
    const params = new URLSearchParams({
      type: customerType,
      services: finalServices.join(","),
    });
    const optionPrefill =
      customerType === "business" && chosenOption?.businessPrefill
        ? chosenOption.businessPrefill
        : chosenOption?.prefill;
    const prefill = encodePrefill(optionPrefill);
    if (prefill) params.set("prefill", prefill);
    if (chosenOption) params.set("val", chosenOption.id);
    if (place.trim()) params.set("ort", place.trim());
    navigate(`${quoteOfferPath(finalServices[0] ?? "")}?${params.toString()}`);
  };

  return (
    <div className="relative py-4 md:py-20 overflow-hidden">
      <HeroBackgroundImage />
      <div className="absolute inset-0 bg-gradient-to-r from-background/95 via-background/85 to-background/60" />

      <div className="page-container relative z-10">
        <div className="grid md:grid-cols-2 gap-4 md:gap-12 items-start md:items-center">
          <div className="min-w-0 space-y-3 md:space-y-6">
            <h1 className="heading-1 text-foreground break-words">
              {title}
            </h1>
            <p className="text-base md:text-xl text-muted-foreground max-w-lg">{subtitle}</p>
          </div>

          <div className="bg-card rounded-2xl shadow-lg p-6 md:p-8 space-y-6">
            <h2 className="heading-2 text-foreground text-center">
              {customerType === "business" ? "Få offerter för företag & BRF" : "Få upp till 4 offerter"}
            </h2>

            {/* Kundtyp – flikar */}
            <div role="tablist" aria-label="Kundtyp" className="grid grid-cols-2 border-b border-border">
              {([
                { type: "private" as const, label: "Privatperson", Icon: User },
                { type: "business" as const, label: "Företag & BRF", Icon: Building2 },
              ]).map(({ type, label, Icon }) => {
                const active = customerType === type;
                return (
                  <button
                    key={type}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    onClick={() => changeCustomerType(type)}
                    className={cn(
                      "-mb-px flex min-w-0 items-center justify-center gap-1.5 border-b-2 px-2 py-2 transition-colors sm:gap-2 sm:px-3 sm:py-3",
                      active
                        ? "border-primary text-primary"
                        : "border-transparent text-muted-foreground hover:text-foreground",
                    )}
                  >
                    <Icon size={16} className="shrink-0" />
                    <span className="whitespace-nowrap text-xs font-semibold leading-tight sm:text-sm md:text-base">
                      {label}
                    </span>
                  </button>
                );
              })}
            </div>

            <div className="space-y-2">
              <Label>
                {customerType === "business"
                  ? "Vad vill ni ha offert på?"
                  : "Vad vill du ha offert på?"}
              </Label>
              <div className="flex flex-wrap gap-2">
                {optionsForType.length
                  ? optionsForType.map((o) => (
                      <button
                        key={o.id}
                        type="button"
                        onClick={() => setOptionId(o.id)}
                        className={cn(
                          "rounded-full border-2 px-3 py-1.5 text-sm transition-colors",
                          optionId === o.id
                            ? "border-primary bg-primary/5 text-foreground"
                            : "border-border hover:border-primary/50 text-muted-foreground",
                        )}
                      >
                        {customerType === "business" && o.businessLabel
                          ? o.businessLabel
                          : o.label}
                      </button>
                    ))
                  : available.map((s) => {
                      const active = selected.includes(s.slug);
                      return (
                        <button
                          key={s.slug}
                          type="button"
                          onClick={() => toggle(s.slug)}
                          className={cn(
                            "rounded-full border-2 px-3 py-1.5 text-sm transition-colors",
                            active
                              ? "border-primary bg-primary/5 text-foreground"
                              : "border-border hover:border-primary/50 text-muted-foreground",
                          )}
                        >
                          {s.shortLabel}
                        </button>
                      );
                    })}
              </div>
            </div>




            <Button size="lg" variant="cta" className="w-full" onClick={start}>
              Få kostnadsfria offerter <ArrowRight className="ml-2" size={18} />
            </Button>

            <div className="flex justify-center items-center gap-2">
              <div className="flex text-yellow-400">
                {[...Array(CUSTOMER_RATING.maxScore)].map((_, i) => (
                  <Star key={i} fill="currentColor" size={16} />
                ))}
              </div>
              <div className="font-bold text-foreground">{CUSTOMER_RATING.scoreText}</div>
              <div className="text-sm text-muted-foreground">({CUSTOMER_RATING.label})</div>
            </div>


            <p className="text-xs text-muted-foreground text-center">
              Kostnadsfritt och utan förpliktelser.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuoteHeroWidget;
